/*
 * packages/brand/qr — the one QR renderer of the merchant surfaces (I-2, gate go 08/10/2026): the print file
 * (FQ-C8), the kiosque « qr », Équipe › Moi and the Caisses link (FQ-I30). Byte mode, error correction M,
 * versions 1 to 10 (up to 213 bytes; a support's URL is about 48). No logo inside, no tricks: cheap scanners
 * read it.
 *
 *   qrMatrix(text) → { size, version, mask, dark(x, y) }
 *   qrPath(text | matrix, offset?) → the dark modules as one SVG path, in modules (to place inside a support's SVG)
 *   qrSvg(text, { margin, fg, bg, label }) → '<svg …>' with its quiet zone (4 modules by default: print)
 *
 * The encoder is apps/player/qr.js's, module for module (the customer page keeps its own copy: it has no
 * bundler and must draw offline). apps/api/test/qr.test.mjs holds the two to the same matrices and to golden
 * matrices frozen from the `qrcode` package. The structure follows ISO/IEC 18004 (finder, timing, alignment,
 * format and version areas, Reed-Solomon over GF(256) with 0x11D, the eight masks and the four penalty rules).
 */

const EXP = new Uint8Array(512), LOG = new Uint8Array(256);
for (let i = 0, x = 1; i < 255; i++) { EXP[i] = x; LOG[x] = i; x <<= 1; if (x & 256) x ^= 0x11d; }
for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
const mul = (a, b) => (a && b ? EXP[LOG[a] + LOG[b]] : 0);

function eccFor(data, n) {
  let g = [1];
  for (let i = 0; i < n; i++) {
    const next = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) { next[j] ^= g[j]; next[j + 1] ^= mul(g[j], EXP[i]); }
    g = next;
  }
  const r = new Array(n).fill(0);
  for (const d of data) {
    const f = d ^ r.shift(); r.push(0);
    for (let i = 0; i < n; i++) r[i] ^= mul(g[i + 1], f);
  }
  return r;
}

// level M, versions 1–10: [EC codewords per block, [blocks, data codewords per block], …]
const BLOCKS = [null,
  [10, [1, 16]], [16, [1, 28]], [26, [1, 44]], [18, [2, 32]], [24, [2, 43]],
  [16, [4, 27]], [18, [4, 31]], [22, [2, 38], [2, 39]], [22, [3, 36], [2, 37]], [26, [4, 43], [1, 44]]];
const ALIGN = [null, [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50]];
const dataCodewords = (v) => BLOCKS[v].slice(1).reduce((s, [n, k]) => s + n * k, 0);

function codewords(bytes) {
  let v = 1;
  while (v <= 10 && 4 + (v < 10 ? 8 : 16) + bytes.length * 8 > dataCodewords(v) * 8) v++;
  if (v > 10) throw new Error('qr: text too long');
  const cap = dataCodewords(v) * 8, bits = [];
  const put = (val, len) => { for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1); };
  put(4, 4); put(bytes.length, v < 10 ? 8 : 16);
  for (const b of bytes) put(b, 8);
  put(0, Math.min(4, cap - bits.length));
  while (bits.length % 8) bits.push(0);
  const data = [];
  for (let i = 0; i < bits.length; i += 8) data.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0));
  for (let pad = 0xec; data.length < cap / 8; pad ^= 0xec ^ 0x11) data.push(pad);
  const [ecLen, ...groups] = BLOCKS[v], blocks = [];
  let at = 0;
  for (const [n, k] of groups) for (let i = 0; i < n; i++) { const d = data.slice(at, at + k); at += k; blocks.push({ d, e: eccFor(d, ecLen) }); }
  const out = [], longest = Math.max(...blocks.map((b) => b.d.length));
  for (let i = 0; i < longest; i++) for (const b of blocks) if (i < b.d.length) out.push(b.d[i]);
  for (let i = 0; i < ecLen; i++) for (const b of blocks) out.push(b.e[i]);
  return { v, out };
}

function build(v, out, mask) {
  const size = v * 4 + 17;
  const m = Array.from({ length: size }, () => new Array(size).fill(false));
  const fn = Array.from({ length: size }, () => new Array(size).fill(false));
  const set = (x, y, dark) => { m[y][x] = dark; fn[y][x] = true; };
  const finder = (cx, cy) => {
    for (let dy = -4; dy <= 4; dy++) for (let dx = -4; dx <= 4; dx++) {
      const x = cx + dx, y = cy + dy, d = Math.max(Math.abs(dx), Math.abs(dy));
      if (x >= 0 && x < size && y >= 0 && y < size) set(x, y, d !== 2 && d !== 4);
    }
  };
  for (let i = 0; i < size; i++) { set(6, i, i % 2 === 0); set(i, 6, i % 2 === 0); }
  finder(3, 3); finder(size - 4, 3); finder(3, size - 4);
  const al = ALIGN[v], last = al.length - 1;
  for (let i = 0; i <= last; i++) for (let j = 0; j <= last; j++) {
    if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) continue;
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) set(al[i] + dx, al[j] + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
  }
  // format areas reserved now, written after the mask is chosen
  const format = (msk) => {
    const d = msk; // level M = 00
    let r = d; for (let i = 0; i < 10; i++) r = (r << 1) ^ ((r >>> 9) * 0x537);
    const bits = ((d << 10) | r) ^ 0x5412, bit = (i) => ((bits >>> i) & 1) === 1;
    for (let i = 0; i <= 5; i++) set(8, i, bit(i));
    set(8, 7, bit(6)); set(8, 8, bit(7)); set(7, 8, bit(8));
    for (let i = 9; i < 15; i++) set(14 - i, 8, bit(i));
    for (let i = 0; i < 8; i++) set(size - 1 - i, 8, bit(i));
    for (let i = 8; i < 15; i++) set(8, size - 15 + i, bit(i));
    set(8, size - 8, true);
  };
  format(0);
  if (v >= 7) {
    let r = v; for (let i = 0; i < 12; i++) r = (r << 1) ^ ((r >>> 11) * 0x1f25);
    const bits = (v << 12) | r;
    for (let i = 0; i < 18; i++) { const b = ((bits >>> i) & 1) === 1, a = size - 11 + (i % 3), c = Math.floor(i / 3); set(a, c, b); set(c, a, b); }
  }
  let i = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) for (let j = 0; j < 2; j++) {
      const x = right - j, y = ((right + 1) & 2) === 0 ? size - 1 - vert : vert;
      if (!fn[y][x] && i < out.length * 8) { m[y][x] = ((out[i >>> 3] >>> (7 - (i & 7))) & 1) === 1; i++; }
    }
  }
  const MASKS = [
    (x, y) => (x + y) % 2 === 0, (x, y) => y % 2 === 0, (x) => x % 3 === 0, (x, y) => (x + y) % 3 === 0,
    (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0, (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
    (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0, (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0];
  const apply = (k) => { for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (!fn[y][x] && MASKS[k](x, y)) m[y][x] = !m[y][x]; };
  const penalty = () => {
    let p = 0, dark = 0;
    const line = (get) => {
      for (let a = 0; a < size; a++) {
        let run = 1;
        for (let b = 1; b <= size; b++) {
          if (b < size && get(a, b) === get(a, b - 1)) run++;
          else { if (run >= 5) p += run - 2; run = 1; }
        }
        for (let b = 0; b + 10 < size; b++) {
          const s = Array.from({ length: 11 }, (_, k) => (get(a, b + k) ? 1 : 0)).join('');
          if (s === '10111010000' || s === '00001011101') p += 40;
        }
      }
    };
    line((a, b) => m[a][b]); line((a, b) => m[b][a]);
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
      if (m[y][x]) dark++;
      if (x < size - 1 && y < size - 1 && m[y][x] === m[y][x + 1] && m[y][x] === m[y + 1][x] && m[y][x] === m[y + 1][x + 1]) p += 3;
    }
    return p + 10 * Math.floor(Math.abs((dark * 100) / (size * size) - 50) / 5);
  };
  if (mask == null) {
    let best = 0, bestP = Infinity;
    for (let k = 0; k < 8; k++) { apply(k); format(k); const p = penalty(); if (p < bestP) { bestP = p; best = k; } apply(k); }
    mask = best;
  }
  apply(mask); format(mask);
  return { size, mask, version: v, dark: (x, y) => m[y][x] };
}

function matrix(text, mask) {
  const { v, out } = codewords(new TextEncoder().encode(String(text)));
  return build(v, out, mask);
}

/**
 * The QR of `text` (a non-empty string; UTF-8 bytes). `mask` forces one of the eight masks (tests only).
 * Nothing to encode throws (vet 08/10): a QR of « undefined » scans, and a support that never works goes unnoticed.
 */
export function qrMatrix(text, mask) {
  if (text == null || text === '') throw new Error('qr: nothing to encode');
  if (typeof text !== 'string') throw new Error('qr: not a string');
  return matrix(text, mask);
}

const isMatrix = (q) => !!q && typeof q === 'object' && Number.isInteger(q.size) && typeof q.dark === 'function';

/** The dark modules as one path (`M x y h n v1 h-n z` per run), shifted by `offset` modules. One path, so no seam between modules in a PDF. */
export function qrPath(text, offset = 0) {
  if (typeof text !== 'string' && !isMatrix(text)) throw new Error(text == null ? 'qr: nothing to encode' : 'qr: not a string or a matrix');
  const q = typeof text === 'string' ? qrMatrix(text) : text;
  let d = '';
  for (let y = 0; y < q.size; y++) {
    for (let x = 0; x < q.size; x++) {
      if (!q.dark(x, y)) continue;
      let run = 1;
      while (x + run < q.size && q.dark(x + run, y)) run++;
      d += `M${x + offset} ${y + offset}h${run}v1h-${run}z`;
      x += run - 1;
    }
  }
  return d;
}

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** Relative luminance of a hex colour (`#rgb` or `#rrggbb`), or null for anything else (a named colour is not checked). */
export function luminance(hex) {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex ?? '').trim());
  if (!m) return null;
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join('') : m[1];
  const lin = (v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * lin(parseInt(h.slice(0, 2), 16)) + 0.7152 * lin(parseInt(h.slice(2, 4), 16)) + 0.0722 * lin(parseInt(h.slice(4, 6), 16));
}

/**
 * Rule 1 of the print rules (handover rule 12, 12/10/2026): the modules are dark and the ground is light, always.
 * A light `fg`, a dark `bg`, or a pair too close to tell apart (contrast under 3:1) throws `qr: inverted` rather than
 * drawing a code many phones will not read. `bg: null` is not checked: the caller promised a white tile.
 */
export function assertDarkOnLight(fg, bg) {
  const lf = luminance(fg), lb = bg == null ? null : luminance(bg);
  if (lf != null && lf > 0.5) throw new Error('qr: inverted');
  if (lb == null) return;
  if (lb < 0.5) throw new Error('qr: inverted');
  if (lf != null && (lb + 0.05) / (lf + 0.05) < 3) throw new Error('qr: inverted');
}

/**
 * A standalone SVG, square, sized by its container (viewBox only). `margin` is the quiet zone in modules: 4 for
 * print (the standard's), 1 inside a tile that is already white. `bg` paints the quiet zone (keep it light: a QR
 * is dark on light, always); `bg: null` leaves it transparent, for a white tile only.
 */
export function qrSvg(text, { margin = 4, fg = '#000', bg = '#fff', label = 'QR code' } = {}) {
  assertDarkOnLight(fg, bg);
  const q = qrMatrix(text), n = q.size + 2 * margin;
  const ground = bg == null ? '' : `<rect width="${n}" height="${n}" fill="${esc(bg)}"/>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges" role="img" aria-label="${esc(label)}">${ground}<path fill="${esc(fg)}" d="${qrPath(q, margin)}"/></svg>`;
}
